import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  limit,
  startAfter,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '@/firebase';
import type { MaintenanceRequest } from '@/types/maintenance';
import { User } from '@/types/users';
import { removeUndefined } from '@/utils/removeUndefined';
import { addWorkspaceAndActivity, createDetailedActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
// Removed broken import
import { loadMockData } from '@/mock';

const MAINTENANCE_REQUESTS_COLLECTION = 'maintenance_requests';

// Helper function to filter mock data
function filterMockData<T extends { title: string; status: string; priority: string; reportedBy: { name: string }; apartmentUnit?: { name: string; id: string }; block: { name: string; id: string } }>(
  data: T[],
  searchTerm?: string,
  status?: string,
  priority?: string,
  blockId?: string,
  unitId?: string
): T[] {
  let filteredData = data;
  
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(item => 
      item.title.toLowerCase().includes(lowercasedSearch) ||
      item.reportedBy.name.toLowerCase().includes(lowercasedSearch) ||
      (item.apartmentUnit?.name.toLowerCase().includes(lowercasedSearch) || false) ||
      item.block.name.toLowerCase().includes(lowercasedSearch)
    );
  }
  
  if (status) {
    filteredData = filteredData.filter(item => item.status === status);
  }
  
  if (priority) {
    filteredData = filteredData.filter(item => item.priority === priority);
  }
  
  if (blockId) {
    filteredData = filteredData.filter(item => item.block.id === blockId);
  }
  
  if (unitId) {
    filteredData = filteredData.filter(item => item.apartmentUnit?.id === unitId);
  }
  
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}

// Types
export interface MaintenanceQueryParams {
  searchTerm?: string;
  status?: string;
  priority?: string;
  blockId?: string;
  unitId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
}

export async function getMaintenanceRequests(firestoreUser: User, params?: MaintenanceQueryParams): Promise<{ maintenanceRequests: MaintenanceRequest[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock maintenance requests with filters', params);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const filteredData = filterMockData(
      await loadMockData<MaintenanceRequest[]>('maintenanceRequests', firestoreUser.language), 
      params?.searchTerm, 
      params?.status, 
      params?.priority, 
      params?.blockId, 
      params?.unitId
    );
    const paginatedData = paginateMockData(filteredData, params?.pageSize, params?.page);
    
    // Use the length of filtered data as total
    const total = filteredData.length;
    
    return {
      maintenanceRequests: paginatedData.data,
      lastDoc: undefined,
      total: total
    };
  }

  console.log('API Call: getMaintenanceRequests', { 
    firestoreUser: firestoreUser.workspace?.id,
    params
  });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('User workspace not found');
    }

    const maintenanceRef = collection(db, MAINTENANCE_REQUESTS_COLLECTION);
    const filterConstraints = [where('workspace.id', '==', firestoreUser.workspace.id)];

    if (params?.searchTerm) {
      filterConstraints.push(
        where('title', '>=', params.searchTerm),
        where('title', '<=', params.searchTerm + '\uf8ff')
      );
    }

    if (params?.status) {
      filterConstraints.push(where('status', '==', params.status));
    }

    if (params?.priority) {
      filterConstraints.push(where('priority', '==', params.priority));
    }

    if (params?.blockId) {
      filterConstraints.push(where('block.id', '==', params.blockId));
    }

    if (params?.unitId) {
      filterConstraints.push(where('apartmentUnit.id', '==', params.unitId));
    }

    let q = query(maintenanceRef, ...filterConstraints);

    if (params?.pageSize) {
      q = query(q, limit(params.pageSize));
    }

    if (params?.lastDoc) {
      q = query(q, startAfter(params.lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const maintenanceRequests: MaintenanceRequest[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      maintenanceRequests.push({
        id: doc.id,
        title: data.title,
        description: data.description,
        status: data.status,
        priority: data.priority,
        reportedBy: data.reportedBy,
        apartmentUnit: data.apartmentUnit,
        block: data.block,
        assignedTo: data.assignedTo,
        activityRecords: data.activityRecords || [],
        workspace: data.workspace,
        attachments: data.attachments || [],
        resolutionNotes: data.resolutionNotes,
        comments: data.comments || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      });
    });

    return {
      maintenanceRequests,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
      total: maintenanceRequests.length
    };
  } catch (error) {
    console.error('Error fetching maintenance requests:', error);
    throw error;
  }
}

export async function getMaintenanceRequestById(id: string, firestoreUser: User): Promise<MaintenanceRequest | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock maintenance request with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockMaintenanceRequests = await loadMockData<MaintenanceRequest[]>('maintenanceRequests', firestoreUser.language);
    const maintenanceRequest = mockMaintenanceRequests.find(m => m.id === id);
    return maintenanceRequest || null;
  }

  console.log('API Call: getMaintenanceRequestById', { id, firestoreUser: firestoreUser.workspace?.id });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('User workspace not found');
    }

    const docRef = doc(db, MAINTENANCE_REQUESTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    
    // Verify workspace access
    if (data.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Access denied');
    }

    return {
      id: docSnap.id,
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      reportedBy: data.reportedBy,
      apartmentUnit: data.apartmentUnit,
      block: data.block,
      assignedTo: data.assignedTo,
      activityRecords: data.activityRecords || [],
      workspace: data.workspace,
      attachments: data.attachments || [],
      resolutionNotes: data.resolutionNotes,
      comments: data.comments || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  } catch (error) {
    console.error('Error fetching maintenance request:', error);
    throw error;
  }
}

export async function addMaintenanceRequest(
  maintenanceRequest: Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt' | 'workspace' | 'activityRecords'>,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addMaintenanceRequest', { 
    maintenanceRequest: { title: maintenanceRequest.title, status: maintenanceRequest.status },
    firestoreUser: firestoreUser.workspace?.id 
  });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('User workspace not found');
    }

    const maintenanceData = addWorkspaceAndActivity(
      removeUndefined(maintenanceRequest),
      firestoreUser,
      'created'
    );

    const docRef = await addDoc(collection(db, MAINTENANCE_REQUESTS_COLLECTION), maintenanceData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding maintenance request:', error);
    throw error;
  }
}

export async function updateMaintenanceRequest(
  id: string,
  data: Partial<Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt' | 'workspace' | 'activityRecords'>>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateMaintenanceRequest', { 
    id, 
    data: Object.keys(data),
    firestoreUser: firestoreUser.workspace?.id 
  });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('User workspace not found');
    }

    const docRef = doc(db, MAINTENANCE_REQUESTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Maintenance request not found');
    }

    const existingData = docSnap.data();
    if (existingData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Access denied');
    }

    const cleanedData = removeUndefined(data);
    const activityRecord = createDetailedActivityRecord(
      'updated',
      existingData,
      { ...existingData, ...cleanedData },
      firestoreUser
    );

    const updateData = {
      ...cleanedData,
      updatedAt: new Date(),
      activityRecords: [
        ...(existingData.activityRecords || []),
        activityRecord
      ]
    };

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating maintenance request:', error);
    throw error;
  }
}

export async function deleteMaintenanceRequest(id: string, firestoreUser: User): Promise<void> {
  console.log('API Call: deleteMaintenanceRequest', { id, firestoreUser: firestoreUser.workspace?.id });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('User workspace not found');
    }

    const docRef = doc(db, MAINTENANCE_REQUESTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Maintenance request not found');
    }

    const data = docSnap.data();
    if (data.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('Access denied');
    }

    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting maintenance request:', error);
    throw error;
  }
} 